import {
  dayjs,
  getLastNCompletedMonthsDateRange,
  getLastNDaysDateRange,
  type Dayjs,
} from "@syncly/utils/date";

import type { DateRange } from "./types";

enum PresetName {
  LAST_WEEK = "lastWeek",
  LAST_MONTH = "lastMonth",
  LAST7 = "last7",
  LAST14 = "last14",
  LAST30 = "last30",
  LAST60 = "last60",
  LAST90 = "last90",
  LAST180 = "last180",
  LAST3_MONTHS = "last3Months",
  LAST6_MONTHS = "last6Months",
  LAST12_MONTHS = "last12Months",
  THIS_MONTH = "thisMonth",
  THIS_YEAR = "thisYear",
}

interface Preset {
  name: PresetName;
  label: string;
}

const PRESETS: Preset[] = [
  { name: PresetName.LAST_WEEK, label: "Last week" },
  { name: PresetName.LAST_MONTH, label: "Last month" },
  { name: PresetName.LAST7, label: "Last 7 days" },
  { name: PresetName.LAST14, label: "Last 14 days" },
  { name: PresetName.LAST30, label: "Last 30 days" },
  { name: PresetName.LAST60, label: "Last 60 days" },
  { name: PresetName.LAST90, label: "Last 90 days" },
  { name: PresetName.LAST180, label: "Last 180 days" },
  { name: PresetName.LAST3_MONTHS, label: "Last 3 months" },
  { name: PresetName.LAST6_MONTHS, label: "Last 6 months" },
  { name: PresetName.LAST12_MONTHS, label: "Last 12 months" },
  { name: PresetName.THIS_MONTH, label: "This month" },
  { name: PresetName.THIS_YEAR, label: "This year" },
];

const DEFAULT_PRESET_NAMES = [
  PresetName.LAST7,
  PresetName.LAST30,
  PresetName.LAST90,
  PresetName.LAST180,
  PresetName.LAST_WEEK,
  PresetName.LAST_MONTH,
  PresetName.THIS_MONTH,
  PresetName.THIS_YEAR,
] as const satisfies readonly PresetName[];

function getPresetDateRange(
  presetName: PresetName,
  referenceDate: Dayjs = dayjs.tz(),
): DateRange {
  const preset = PRESETS.find(({ name }) => name === presetName);

  if (!preset) {
    throw new Error(`Unknown date range preset: ${presetName}`);
  }

  switch (preset.name) {
    case PresetName.LAST_WEEK:
      return {
        from: referenceDate.subtract(1, "week").startOf("week"),
        to: referenceDate.subtract(1, "week").endOf("week"),
      };
    case PresetName.LAST_MONTH:
      return getLastNCompletedMonthsDateRange(1, referenceDate);
    case PresetName.LAST7:
      return getLastNDaysDateRange(7, referenceDate);
    case PresetName.LAST14:
      return getLastNDaysDateRange(14, referenceDate);
    case PresetName.LAST30:
      return getLastNDaysDateRange(30, referenceDate);
    case PresetName.LAST60:
      return getLastNDaysDateRange(60, referenceDate);
    case PresetName.LAST90:
      return getLastNDaysDateRange(90, referenceDate);
    case PresetName.LAST180:
      return getLastNDaysDateRange(180, referenceDate);
    case PresetName.LAST3_MONTHS:
      return getLastNCompletedMonthsDateRange(3, referenceDate);
    case PresetName.LAST6_MONTHS:
      return getLastNCompletedMonthsDateRange(6, referenceDate);
    case PresetName.LAST12_MONTHS:
      return getLastNCompletedMonthsDateRange(12, referenceDate);
    case PresetName.THIS_MONTH:
      return {
        from: referenceDate.startOf("month"),
        to: referenceDate.endOf("month"),
      };
    case PresetName.THIS_YEAR:
      return {
        from: referenceDate.startOf("year"),
        to: referenceDate.endOf("day"),
      };
  }
}

export { DEFAULT_PRESET_NAMES, PRESETS, PresetName, getPresetDateRange };
export type { Preset };
